import React from "react";
import { CheckCircle2, Clock, ShoppingBag, Package, Sparkles, Truck, MapPin, Users, ShieldCheck, School, HeartHandshake } from "lucide-react";

/**
 * ListingBadges — reusable badge stack for listing cards.
 *
 * Renders a consistent set of pills across every surface (Browse, Search,
 * Seller Dashboard, Listing Detail). Callers can trim the set via the
 * `only` prop; otherwise every applicable badge renders in a stable order:
 *
 *   1. Status:    LIVE / RESERVED (with · PENDING PARENT APPROVAL) / SOLD
 *   2. New:       JUST POSTED (< 48h)
 *   3. Category:  JUNIOR HUB / TINY TRADERS / TWEEN COLLECTORS / TEENS
 *   4. Condition: BRAND NEW / ALMOST NEW / USED - GOOD CONDITION / WELL LOVED
 *   5. Parcel:    BULKY ITEM / COLLECTION ONLY / DELIVERY ONLY / ROYAL MAIL
 *   6. Posted by: PARENT POSTED
 *
 * Every pill is high-contrast, distinct in colour, kebab-cased data-testid,
 * and wraps cleanly on narrow viewports (uses flex-wrap + gap-1.5).
 */

const CATEGORY_LABELS = {
  baby_toddler:  { label: "Tiny Traders",     tone: "pink" },
  junior_hub:    { label: "Junior Hub",       tone: "sky" },
  collectors:    { label: "Tween Collectors", tone: "violet" },
  teen:          { label: "Teens",            tone: "indigo" },
  exchange:      { label: "Exchange",         tone: "amber" },
  free:          { label: "Freebie",          tone: "emerald" },
  clothing:      { label: "Clothing",         tone: "rose" },
  other:         { label: "Other",            tone: "slate" },
};

const CONDITION_LABELS = {
  brand_new:   { label: "Brand New", tone: "emerald" },
  excellent:   { label: "Brand New", tone: "emerald" },
  almost_new:  { label: "Almost New", tone: "emerald" },
  like_new:    { label: "Almost New", tone: "emerald" },
  used_good:   { label: "Used - Good Condition", tone: "orange" },
  good:        { label: "Used - Good Condition", tone: "orange" },
  played_with: { label: "Used - Good Condition", tone: "orange" },
  well_loved:  { label: "Well Loved", tone: "amber" },
  fair:        { label: "Well Loved", tone: "amber" },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  amber:   "bg-amber-100 text-amber-900 border-amber-200",
  orange:  "bg-orange-100 text-orange-900 border-orange-200",
  slate:   "bg-slate-100 text-slate-700 border-slate-200",
  sky:     "bg-sky-100 text-sky-900 border-sky-200",
  violet:  "bg-violet-100 text-violet-900 border-violet-200",
  indigo:  "bg-indigo-100 text-indigo-900 border-indigo-200",
  pink:    "bg-pink-100 text-pink-900 border-pink-200",
  rose:    "bg-rose-100 text-rose-900 border-rose-200",
  red:     "bg-red-100 text-red-900 border-red-200",
  teal:    "bg-teal-100 text-teal-900 border-teal-200",
};

const Pill = ({ tone = "slate", icon: Icon, label, testid }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap ${TONE_CLASSES[tone] || TONE_CLASSES.slate}`}
    data-testid={testid}
  >
    {Icon ? <Icon className="w-3 h-3" /> : null}
    {label}
  </span>
);

export default function ListingBadges({ listing, only, className = "", testid }) {
  if (!listing) return null;
  const show = (key) => !only || only.includes(key);
  const status = listing.status;
  const badges = [];

  // Status ---------------------------------------------------------------
  if (show("status")) {
    if (status === "sold") {
      badges.push(
        <Pill key="status" tone="slate" icon={ShoppingBag} label="Sold" testid={`badge-status-sold-${listing.id}`} />
      );
    } else if (status === "reserved") {
      badges.push(
        <Pill
          key="status"
          tone="amber"
          icon={Clock}
          label={
            listing.fulfillment_pending_parent
              ? "Reserved · Pending Parent Approval"
              : "Reserved"
          }
          testid={`badge-status-reserved-${listing.id}`}
        />
      );
    } else if (status === "live" || status === "public") {
      badges.push(
        <Pill key="status" tone="emerald" icon={CheckCircle2} label="Live" testid={`badge-status-live-${listing.id}`} />
      );
    }
  }

  // Just Posted ----------------------------------------------------------
  if (show("new") && listing.is_new && status !== "sold" && status !== "reserved") {
    badges.push(
      <Pill key="new" tone="rose" icon={Sparkles} label="Just Posted" testid={`badge-new-${listing.id}`} />
    );
  }

  // Category / Age hub ---------------------------------------------------
  if (show("category")) {
    // Iteration 274 — clothing listings carry their real category in
    // `category_web` (upstream only knows "other").
    const cat = CATEGORY_LABELS[listing.category_web || listing.category];
    if (cat) {
      badges.push(
        <Pill key="cat" tone={cat.tone} label={cat.label} testid={`badge-category-${listing.category_web || listing.category}-${listing.id}`} />
      );
    }
  }

  // Clothing attributes ----------------------------------------------------
  // Iteration 274 — Size · Gender · Brand pill for clothing listings so
  // buyers can scan the key attributes straight off the card.
  if (show("clothing") && (listing.category_web === "clothing" || listing.category === "clothing")) {
    const bits = [listing.clothing_size, listing.clothing_gender, listing.clothing_brand].filter(Boolean);
    if (bits.length) {
      const genderLabel = { boys: "Boys", girls: "Girls", unisex: "Unisex", baby: "Baby" }[listing.clothing_gender] || listing.clothing_gender;
      const label = [listing.clothing_size, listing.clothing_gender && genderLabel, listing.clothing_brand].filter(Boolean).join(" · ");
      badges.push(
        <Pill key="clothing" tone="rose" label={label} testid={`badge-clothing-${listing.id}`} />
      );
    }
  }

  // Condition ------------------------------------------------------------
  if (show("condition")) {
    const cond = CONDITION_LABELS[listing.condition];
    if (cond) {
      badges.push(
        <Pill key="cond" tone={cond.tone} icon={ShieldCheck} label={cond.label} testid={`badge-condition-${listing.id}`} />
      );
    }
  }

  // Parcel / fulfillment -------------------------------------------------
  // Split into two sub-buckets so surfaces (like Listing Detail) can
  // render Royal Mail on the "core status" row and the parent-chosen
  // fulfillment tags on a dedicated second row.
  if (show("royal_mail")) {
    if (
      listing.package_size &&
      ["small", "medium", "large"].includes(listing.package_size) &&
      listing.shipping_available
    ) {
      badges.push(
        <Pill key="rm" tone="slate" icon={Truck} label="Royal Mail" testid={`badge-royal-mail-${listing.id}`} />
      );
    }
  }
  if (show("parcel")) {
    if (listing.package_size === "bulky") {
      badges.push(
        <Pill key="bulky" tone="amber" icon={Package} label="Bulky Item" testid={`badge-bulky-${listing.id}`} />
      );
    }
    // Iteration 208 — surface every parent-selected fulfillment mode
    // and its Parent Handoff sub-badges so buyers see the seller's
    // preferred handoff method at a glance on every card view.
    const choices = Array.isArray(listing.parent_fulfillment_choices) ? listing.parent_fulfillment_choices : [];
    if (choices.includes("parent_handoff")) {
      badges.push(
        <Pill key="ph" tone="indigo" icon={Users} label="Parent Handoff" testid={`badge-parent-handoff-${listing.id}`} />
      );
    }
    // Sub-badges only render alongside Parent Handoff (they're
    // seller-perspective tags under that mode).
    if (choices.includes("parent_handoff") && choices.includes("collection_only")) {
      badges.push(
        <Pill key="col" tone="emerald" icon={MapPin} label="Collection Only" testid={`badge-collection-only-${listing.id}`} />
      );
    }
    if (choices.includes("parent_handoff") && choices.includes("delivery_only")) {
      badges.push(
        <Pill key="del" tone="teal" icon={Truck} label="Delivery Only" testid={`badge-delivery-only-${listing.id}`} />
      );
    }
    if (choices.includes("school_gate")) {
      badges.push(
        <Pill key="sg" tone="sky" icon={School} label="At/After School" testid={`badge-after-school-${listing.id}`} />
      );
    }
    if (choices.includes("local_shop")) {
      badges.push(
        <Pill key="ls" tone="violet" icon={MapPin} label="Local Shop Pick-Up" testid={`badge-local-shop-${listing.id}`} />
      );
    }
    // Royal Mail Post is already rendered by the `royal_mail` block
    // above (guarded by `shipping_available`) — don't duplicate it here.
  }

  // Parent posted --------------------------------------------------------
  if (show("parent_posted") && listing.parent_posted) {
    badges.push(
      <Pill key="pp" tone="sky" icon={Users} label="Posted by Parent" testid={`badge-parent-posted-${listing.id}`} />
    );
  }

  // Fundraising for a cause ------------------------------------------------
  // Iteration 286 — listing tagged to a verified cause sub-account.
  if (show("fundraise") && listing.cause_kid_id) {
    badges.push(
      <Pill key="fundraise" tone="rose" icon={HeartHandshake} label={`Fundraising for ${listing.cause_name || "a Cause"}`} testid={`badge-fundraise-${listing.id}`} />
    );
  }

  // Verified PTA / Cause ---------------------------------------------------
  // Iteration 284 — listings posted by an organisation/cause sub-account
  // (seller_account_type is attached web-tier from the kid_metadata shim).
  // Iteration 285 — fake-badge prevention: only VERIFIED orgs render it.
  if (show("cause") && listing.seller_account_type === "organization" && listing.seller_org_verified) {
    badges.push(
      <Pill key="cause" tone="teal" icon={HeartHandshake} label="Verified PTA / Cause" testid={`badge-cause-${listing.id}`} />
    );
  }

  if (!badges.length) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid={testid || `listing-badges-${listing.id}`}>
      {badges}
    </div>
  );
}
